import { QueryKeys } from '@/constants/QueryKeys';
import { ReportApi } from '@/utilities/api/ReportApi';
import { ReportEndpoint } from '@api/endpoints';
import { ReportStatus } from '@shared/enums';
import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';

const POLL_INTERVAL_MS = 2000;

export function useReportGeneration (
  retailerId: string,
  onPollError?: () => void,
) {
  const [reportId, setReportId] = useState<string | null>(null);

  const {
    mutateAsync: requestReport,
    isLoading: isRequesting,
  } = useMutation(
    (request: ReportEndpoint.Create.Request) => (
      ReportApi.create(retailerId, request).then((report) => setReportId(report.id))
    ),
  );

  const { data: report, isError } = useQuery(
    [QueryKeys.GET_REPORT, reportId],
    () => ReportApi.get(retailerId, reportId as string),
    {
      enabled: !!reportId,
      onError: onPollError,
      refetchInterval: (data) => (
        data && [ReportStatus.Completed, ReportStatus.Failed].includes(data.status)
          ? false
          : POLL_INTERVAL_MS
      ),
    },
  );

  /*
   * When create resolves, isRequesting drops to false while the poll for the new id is
   * still in flight, leaving `report` undefined. Without covering that window callers
   * flicker back to their initial state for the length of a round trip.
   */
  const isAwaitingFirstPoll = !!reportId && !report && !isError;
  const isGenerating = isRequesting
    || isAwaitingFirstPoll
    || report?.status === ReportStatus.Pending
    || report?.status === ReportStatus.Processing;

  const download = async () => {
    const { url } = await ReportApi.getDownloadUrl(retailerId, reportId as string);
    window.location.assign(url);
  };

  const clearReport = () => setReportId(null);

  return { clearReport, download, isGenerating, report, requestReport };
}
